import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  Users, Building2, ShieldCheck, Stethoscope,
  MapPin, Landmark, UserCircle, ChevronRight,
  AlertCircle, CheckCircle, Clock
} from "lucide-react";

const emptyData = {
  sarpanchOrMLA: "",
  partyName: "",
  partyOfficeAddress: "",
  policeASI: "",
  policeHawaldar: "",
  nearbyHospital: "",
  stateGovernment: {
    party: "",
    cmName: "",
  },
};

const FamilyInformationForm = () => {
  const [data, setData] = useState(emptyData);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStateGovChange = (subField, value) => {
    setData((prev) => ({
      ...prev,
      stateGovernment: { ...prev.stateGovernment, [subField]: value },
    }));
  };

  useEffect(() => {
    fetchAPi();
  }, []);

  const fetchAPi = async () => {
    setLoading(true);
    try {
      const response = await makeRequestAuth("get", "/user/getLocalAuthority");
      if (response.data) setPreview(response.data);
    } catch {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let list = [];
    if (!data.sarpanchOrMLA.trim()) list.push("Sarpanch / MLA Name is required");
    if (!data.partyName.trim()) list.push("Party Name is required");
    if (!data.partyOfficeAddress.trim()) list.push("Party Office Address is required");
    if (!data.policeASI.trim()) list.push("Police ASI Name is required");
    if (!data.policeHawaldar.trim()) list.push("Police Hawaldar Name is required");
    if (!data.nearbyHospital.trim()) list.push("Nearby Hospital is required");
    if (!data.stateGovernment.party.trim()) list.push("State Government Party is required");
    if (!data.stateGovernment.cmName.trim()) list.push("Chief Minister Name is required");
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo(0, 0);
      return;
    }
    setErrors([]);
    setLoading(true);
    try {
      await makeRequestAuth("post", "/user/createOrUpdateLocalAuthority", data);
      setPreview(data);
      showSuccessToast("Details saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={14} /> },
    approve: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} /> },
    reject: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={14} /> },
  };

  return (
    <div className="max-w-5xl mx-auto py-6 antialiased">
      <ToastContainer />

      {/* Modern Loader */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
            <span className="font-bold text-[#132b45]">Updating Records...</span>
          </div>
        </div>
      )}

      {/* ERROR BOX */}
      {errors.length > 0 && (
        <div className="mb-8 bg-rose-50 border border-rose-200 rounded-2xl p-5 animate-in slide-in-from-top-4">
          <div className="flex items-center gap-2 text-rose-700 mb-3 font-bold">
            <AlertCircle size={20} />
            <span>Submission Errors</span>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 list-inside">
            {errors.map((e, i) => (
              <li key={i} className="text-rose-600 text-sm">{e}</li>
            ))}
          </ul>
        </div>
      )}

      {preview && preview.sarpanchOrMLA ? (
        /* ================= PREVIEW MODE ================= */
        <div className="space-y-8 animate-in fade-in duration-700">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-10 py-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black text-[#132b45] uppercase tracking-tight">Authority Overview</h3>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Verified Local Information</p>
              </div>
              <div className={`flex items-center gap-2 px-5 py-2 rounded-full border shadow-sm font-bold text-xs uppercase tracking-widest ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.color || STATUS_CONFIG.pending.color}`}>
                {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon || STATUS_CONFIG.pending.icon}
                {preview?.status || "Pending"}
              </div>
            </div>

            <div className="p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                <PreviewItem icon={<Landmark className="text-indigo-500" />} label="Sarpanch / MLA" value={preview.sarpanchOrMLA} />
                <PreviewItem icon={<Users className="text-blue-500" />} label="Political Party" value={preview.partyName} />
                <PreviewItem icon={<ShieldCheck className="text-slate-500" />} label="Police ASI" value={preview.policeASI} />
                <PreviewItem icon={<ShieldCheck className="text-slate-400" />} label="Police Hawaldar" value={preview.policeHawaldar} />
                <PreviewItem icon={<Stethoscope className="text-rose-500" />} label="Nearby Hospital" value={preview.nearbyHospital} />
                <PreviewItem icon={<UserCircle className="text-amber-500" />} label="Chief Minister" value={preview?.stateGovernment?.cmName} />

                <div className="md:col-span-2 lg:col-span-3 bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100">
                  <PreviewItem icon={<MapPin className="text-indigo-600" />} label="Party Office Address" value={preview.partyOfficeAddress} />
                </div>
              </div>
            </div>
          </div>

          {preview?.status === "reject" && (
            <div className="flex justify-center">
              <button
                onClick={() => { setPreview(null); setData(emptyData); }}
                className="group flex items-center gap-3 bg-[#132b45] hover:bg-black text-white px-10 py-4 rounded-2xl font-bold shadow-xl transition-all active:scale-95"
              >
                Edit Records <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ================= FORM MODE ================= */
        <form onSubmit={handleSubmit} className="space-y-10 animate-in slide-in-from-bottom-8 duration-700">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 p-10 shadow-sm">
            <div className="flex items-center gap-4 mb-10 border-b border-slate-50 pb-6">
              <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                <Building2 size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-[#132b45]">Local Authority Details</h2>
                <p className="text-slate-400 text-sm font-medium">Please provide accurate local governance information</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <Input label="Sarpanch / MLA Name *" value={data.sarpanchOrMLA} onChange={(v) => handleChange("sarpanchOrMLA", v)} />
              <Input label="Political Party Name *" value={data.partyName} onChange={(v) => handleChange("partyName", v)} />

              <div className="md:col-span-2">
                <Textarea label="Party Office Address *" value={data.partyOfficeAddress} onChange={(v) => handleChange("partyOfficeAddress", v)} />
              </div>

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100">
                <Input label="Police ASI Name *" value={data.policeASI} onChange={(v) => handleChange("policeASI", v)} />
                <Input label="Police Hawaldar Name *" value={data.policeHawaldar} onChange={(v) => handleChange("policeHawaldar", v)} />
              </div>

              <div className="md:col-span-2">
                <Input label="Nearby Hospital *" value={data.nearbyHospital} onChange={(v) => handleChange("nearbyHospital", v)} />
              </div>

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 bg-indigo-50/30 p-6 rounded-[2rem] border border-indigo-50">
                <Input label="State Government Party *" value={data.stateGovernment.party} onChange={(v) => handleStateGovChange("party", v)} />
                <Input label="Chief Minister Name *" value={data.stateGovernment.cmName} onChange={(v) => handleStateGovChange("cmName", v)} />
              </div>
            </div>

            <div className="mt-12 flex justify-end">
              <button
                type="submit"
                className="w-full sm:w-auto bg-[#132b45] hover:bg-black text-white px-16 py-4 rounded-2xl font-black shadow-2xl shadow-blue-900/20 transition-all active:scale-95 flex items-center justify-center gap-3"
              >
                Save Family Records
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

/* --- REUSABLE SUB-COMPONENTS --- */

const Input = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-bold text-slate-700 ml-1 uppercase tracking-widest text-[10px]">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3.5 focus:border-indigo-500 focus:bg-white outline-none transition-all"
      placeholder={`Enter ${label.replace('*', '').trim()}`}
    />
  </div>
);

const Textarea = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-bold text-slate-700 ml-1 uppercase tracking-widest text-[10px]">{label}</label>
    <textarea
      rows="3"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3.5 focus:border-indigo-500 focus:bg-white outline-none transition-all resize-none"
      placeholder={`Enter full address...`}
    />
  </div>
);

const PreviewItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="mt-1 p-2 bg-slate-50 rounded-xl group-hover:bg-white transition-colors">
      {React.cloneElement(icon, { size: 20 })}
    </div>
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest font-black text-slate-400 leading-none mb-1.5">{label}</span>
      <span className="text-[#132b45] font-bold break-words leading-tight">{value || "—"}</span>
    </div>
  </div>
);

export default FamilyInformationForm;